import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import {
  Users, UserGear, EnvelopeSimple, ChatCircleDots,
  HardDrives, ArrowsClockwise, Pulse, Warning,
} from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { auditLabel, needsAttention } from '../../lib/auditLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

const ago = (iso) => {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); } catch (e) { return ''; }
};

/** A card; a button when it leads somewhere useful. */
const StatCard = ({ icon: Icon, label, value, sub, testId, to }) => {
  const navigate = useNavigate();
  const Tag = to ? 'button' : 'div';
  return (
    <Tag
      type={to ? 'button' : undefined}
      onClick={to ? () => navigate(to) : undefined}
      className={[
        'p-4 border border-border rounded-xl bg-card/85 backdrop-blur-sm text-left min-w-0',
        to ? 'transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : '',
      ].join(' ')}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="w-4 h-4" style={{ color: 'hsl(var(--gold))' }} aria-hidden="true" />
        <span className="text-[11px] font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-heading font-semibold tabular-nums" style={{ color: 'hsl(var(--gold-pale))' }}>{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Tag>
  );
};

/**
 * The Founder Desk's content. Every figure is a field of /superadmin/stats,
 * which is superadmin-only; the health chip is /api/health and only says what
 * that endpoint measured. Nothing is estimated or invented.
 *
 * Recent activity shows what happened, by whom and when -- never the details
 * field, which can carry file names and ids. Those stay in the full Audit Log.
 */
const OverviewStats = () => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedAt, setLoadedAt] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    // Separately, so a health check that fails cannot blank the figures.
    const healthReq = axios.get(`${API_URL}/api/health`, { withCredentials: true })
      .then((r) => setHealth(r.data))
      // A degraded service answers 503 WITH its report -- the one time the
      // chip matters most, so read the body rather than dropping it.
      .catch((e) => setHealth(e.response?.data?.status ? e.response.data : { status: 'unreachable' }));
    try {
      const { data } = await axios.get(`${API_URL}/api/superadmin/stats`, { withCredentials: true });
      setStats(data);
      setLoadedAt(new Date().toISOString());
    } catch (e) {
      setError(e.response?.data?.detail || 'The overview could not be loaded.');
    } finally {
      await healthReq;
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="overview-stats">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'hsl(var(--gold))' }}>
          Overview
        </h2>
        <div className="flex items-center gap-2">
          {health && (
            <span
              className={[
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs',
                health.status === 'ok'
                  ? 'border-border bg-card/80 text-foreground'
                  : 'border-destructive/40 bg-destructive/10 text-destructive',
              ].join(' ')}
              data-testid="system-health-badge"
            >
              <Pulse className="w-3 h-3" weight="fill" aria-hidden="true" />
              {health.status === 'ok' && 'Service responding · database up'}
              {health.status === 'degraded' && `Service degraded · database ${health.database}`}
              {health.status === 'unreachable' && 'Health check not answering'}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-stats">
            <ArrowsClockwise className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          <Warning className="w-4 h-4 mt-0.5 shrink-0" weight="fill" aria-hidden="true" />
          <span>{error}{stats ? ' The figures below are from the last successful load.' : ''}</span>
        </div>
      )}

      {!stats && loading && (
        <p className="text-center py-8 text-sm text-muted-foreground" role="status">Loading the overview…</p>
      )}

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard
              icon={Users} label="Members" testId="stat-users" to="/admin/members"
              value={stats.users.total}
              sub={`${stats.users.active} active · ${stats.users.inactive} inactive`}
            />
            <StatCard
              icon={Users} label="Assigned" testId="stat-assignment" to="/admin/more/assignments"
              value={stats.users.assigned}
              sub={`${stats.users.unassigned} unassigned`}
            />
            <StatCard
              icon={UserGear} label="Operators" testId="stat-operators" to="/admin/more/operators"
              value={stats.operators.total}
              sub={`${stats.operators.active} active`}
            />
            <StatCard
              icon={EnvelopeSimple} label="Invites" testId="stat-invites" to="/admin/invites"
              value={stats.invites.pending}
              sub="pending"
            />
            <StatCard
              icon={ChatCircleDots} label="Support threads" testId="stat-threads" to="/dashboard"
              value={stats.threads.total}
              sub={`${stats.threads.awaiting_reply} awaiting reply`}
            />
            <StatCard
              icon={HardDrives} label="Media" testId="stat-media"
              value={formatBytes(stats.media.storage_bytes)}
              sub={`${stats.media.count} files`}
            />
          </div>
          {loadedAt && (
            <p className="text-[11px] text-muted-foreground" data-testid="overview-loaded-at">
              Figures as of {ago(loadedAt)}. Refresh to update.
            </p>
          )}

          <section>
            <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'hsl(var(--gold))' }}>
              Recent activity
            </h2>
            <div className="rounded-xl border border-border bg-card/85 backdrop-blur-sm divide-y divide-border">
              {stats.recent_audit.length === 0 && (
                <p className="text-sm text-muted-foreground p-4 text-center">Nothing recorded yet.</p>
              )}
              {stats.recent_audit.map((log) => (
                <div key={log.id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`recent-${log.id}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    {needsAttention(log.action) && (
                      <Warning className="w-3.5 h-3.5 shrink-0 text-destructive" weight="fill" aria-label="Security event" />
                    )}
                    <span className="truncate text-foreground">{auditLabel(log.action)}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{log.actor || ''}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{ago(log.created_at)}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default OverviewStats;
